/* eslint-disable react/display-name */
import React, {
  createContext,
  Fragment,
  JSXElementConstructor,
  ReactElement,
  useCallback,
  useContext,
  useMemo,
} from "react";
import { useLocation, useNavigate } from "react-router";
import groupBy from "lodash/groupBy";
import { syllableParity } from "./syllableParity";
import { sameTextAppearance } from "./textAppearance";
import { createRhesePresentations, intersectsRhese } from "./rhesePresentation";
import classNames from "classnames";
import { Tooltip } from "antd";
import "./sharedBookStyle.less";

import { SearchContext } from "../Search";
import { LetterKind, createPageConverter } from "../../api/formatters";
import { useI18n } from "../../../../../i18n";
import { Rhese, Letter, checkIfPunctuation } from "../../../types";
import { SubToPageContent_page_rheses } from "../../api/SubToPageContent";
import { isFormatTab } from "../../../utils/common";
import {
  SubscribeToExtendedPage_extendedPage,
  SubscribeToExtendedPage_extendedPage_definition,
} from "../../api/__generated__/SubscribeToExtendedPage";

export const RheseOffset = createContext(0);

const i18nKey = "project.existing.bookRenderer.paraghapLabel";

const ParagrapLabel = {
  CENTRE: `${i18nKey}.center`,
  CITATION: `${i18nKey}.citation`,
  COURRIER: `${i18nKey}.mail`,
  DIALOGUE: `${i18nKey}.dialogue`,
  DIDASCALIE: `${i18nKey}.didascalie`,
  DROITE: `${i18nKey}.right`,
  EMAIL: `${i18nKey}.email`,
  ESPACE: `${i18nKey}.space`,
  GRAS: `${i18nKey}.bold`,
  INTRO: `${i18nKey}.intro`,
  NORMAL: `${i18nKey}.normal`,
  NOTE: `${i18nKey}.note`,
  RESUME: `${i18nKey}.résumé`,
  TEXTO: `${i18nKey}.text`,
  TITRE_1: `${i18nKey}.title1`,
  TITRE_2: `${i18nKey}.title2`,
  TITRE_3: `${i18nKey}.title3`,
  TITRE_4: `${i18nKey}.title4`,
  TITRE_5: `${i18nKey}.title5`,
  TITRE_6: `${i18nKey}.title6`,
  TITRE1: `${i18nKey}.title1`,
  TITRE2: `${i18nKey}.title2`,
  TITRE3: `${i18nKey}.title3`,
  TITRE4: `${i18nKey}.title4`,
  TITRE5: `${i18nKey}.title5`,
  TITRE6: `${i18nKey}.title6`,
  VERS: `${i18nKey}.vers`,
  LIST_ORDERED: `${i18nKey}.listOrdered`,
  LIST_UNORDERED: `${i18nKey}.listUnordered`,
  LIST_DEFINITION: `${i18nKey}.listDefinition`,
};

type LettersGroup = {
  isEven?: boolean;
  letters: Letter[];
  definitionOccurence?: {
    id: string;
  };
};

type DefinitionWithGroups = {
  definition: SubscribeToExtendedPage_extendedPage_definition;
  groups: LettersGroup[];
};

export const isDefinitionWithGroups = (val: any): val is DefinitionWithGroups =>
  val?.groups && val?.definition;

const convertRhese = (
  rhese: Rhese,
  definitions: SubscribeToExtendedPage_extendedPage_definition[],
  parity: readonly (boolean | undefined)[],
  definitionMode: boolean,
) => {
  const rheseDefinitions = definitionMode
    ? definitions.filter((definition) => intersectsRhese(rhese, definition))
    : [];

  let lettersGroups = rhese.letters.map((letter, index) => {
    return {
      isEven: parity[index],
      letters: [letter],
    };
  });

  const result: (LettersGroup[] | DefinitionWithGroups)[] = [];
  const orderedDefinitions: SubscribeToExtendedPage_extendedPage_definition[] = [
    ...rheseDefinitions,
  ];
  orderedDefinitions.sort((a, b) => a.anchors[0].utf16Start - b.anchors[0].utf16Start);

  let currentDefinition = orderedDefinitions.shift();
  let currentGroup: LettersGroup[] | DefinitionWithGroups = [];

  for (let lettersGroup of lettersGroups) {
    let letter = lettersGroup.letters[0];

    if (
      currentDefinition &&
      letter.absoluteTextPosition >= currentDefinition.anchors[0].utf16Start &&
      letter.absoluteTextPosition <
        currentDefinition.anchors[0].utf16Start + currentDefinition.anchors[0].utf16Size
    ) {
      if (
        isDefinitionWithGroups(currentGroup) &&
        currentGroup.definition.data.id === currentDefinition.data.id
      ) {
        currentGroup.groups.push({
          isEven: lettersGroup.isEven,
          letters: [letter],
          definitionOccurence: currentGroup.groups[0].definitionOccurence,
        });
      } else {
        result.push(currentGroup);
        currentGroup = {
          definition: currentDefinition,
          groups: [lettersGroup],
        };
      }
      if (
        letter.absoluteTextPosition ===
        currentDefinition.anchors[0].utf16Start + currentDefinition.anchors[0].utf16Size - 1
      ) {
        currentDefinition = orderedDefinitions.shift();
      }
    } else {
      if (isDefinitionWithGroups(currentGroup)) {
        result.push(currentGroup);
        currentGroup = [lettersGroup];
      } else {
        currentGroup.push(lettersGroup);
      }
    }
  }

  if (result[result.length - 1] !== currentGroup) {
    result.push(currentGroup);
  }

  return result.flat();
};

const RenderLetter = React.memo(function RenderLetter({
  letter,
  i,
  evenOdd,
  isPunctuation,
  hasSyllable,
  enableCustomCssTooltip,
  highlightSpaces = false,
}: {
  letter: Letter;
  i: number;
  evenOdd: "" | "impair" | "pair";
  isPunctuation: boolean;
  hasSyllable: boolean;
  enableCustomCssTooltip: boolean;
  highlightSpaces?: boolean;
}) {
  let phoneme = "";
  if (letter.phoneme && !letter.isMute) {
    phoneme = "phoneme " + letter.phoneme;
  }

  let customColoration = "";
  if (letter.customColoration) {
    customColoration = "customColoration " + "color" + letter.customColoration;
  }

  const tagProps = {
    "data-index": i,
    "data-id": letter.id,
    className: classNames(
      isPunctuation
        ? [
            "ponctuation",
            "circleContainerTooltipClasses",
            letter.kind?.filter((k) => LetterKind.CITATION === k),
          ]
        : [
            "letter",
            "circleContainerTooltipClasses",
            ...(highlightSpaces && (letter.char === " " || letter.char === "\xa0")
              ? ["highlightedSpace"]
              : []),
            evenOdd,
            phoneme,
            customColoration,
            letter.kind,
            {
              muet: letter.isMute,
              insecable: letter.char === "\xa0",
              syllable: hasSyllable,
            },
          ],
    ),
  };

  let Tag;

  if (letter.kind?.includes(LetterKind.EXPOSANT)) {
    Tag = "sup" as keyof JSX.IntrinsicElements;
  } else if (letter.kind?.includes(LetterKind.INDICE)) {
    Tag = "sub" as keyof JSX.IntrinsicElements;
  } else {
    Tag = "span" as keyof JSX.IntrinsicElements;
  }

  if (!enableCustomCssTooltip || !letter.cssClasses) {
    return <Tag {...tagProps}>{letter.char}</Tag>;
  }

  return (
    <Tooltip title={letter.cssClasses}>
      <Tag {...tagProps}>
        {/* The <span/> below is needed to avoid a bad display when rheses on the same line both have and have not <Tooltip/>'s title */}
        <>
          {letter.char}
          {!!letter.cssClasses && <span className="circleTooltipClasses" />}
        </>
      </Tag>
    </Tooltip>
  );
});
const isEvenOdd = (bool?: boolean) => {
  let isEven: "" | "pair" | "impair" = "";
  if (bool === true) {
    isEven = "pair";
  }
  if (bool === false) {
    isEven = "impair";
  }
  return isEven;
};

const RenderText = React.memo(function RenderText({
  groupedLetters,
  letters,
  letterParity,
  enableCustomCssTooltip,
  highlightSpaces,
}: {
  groupedLetters: (LettersGroup | DefinitionWithGroups)[] | null;
  letters: Letter[];
  letterParity: readonly (boolean | undefined)[];
  enableCustomCssTooltip: boolean;
  highlightSpaces: boolean;
}) {
  let counter = 0;
  return (
    <Fragment>
      {!groupedLetters
        ? letters.map((letter, i) => (
            <RenderLetter
              key={letter.position}
              letter={letter}
              i={i}
              evenOdd={isEvenOdd(letterParity[i])}
              isPunctuation={!!checkIfPunctuation(letter.char)}
              hasSyllable={letter.syllableId !== undefined && letter.syllableId !== null}
              enableCustomCssTooltip={enableCustomCssTooltip}
              highlightSpaces={highlightSpaces}
            />
          ))
        : groupedLetters.map((lettersGroup) => {
            if (isDefinitionWithGroups(lettersGroup)) {
              return (
                <Tooltip
                  key={`definition-${lettersGroup.groups[0].letters[0].position}`}
                  title={
                    lettersGroup.definition.data.description ||
                    lettersGroup.definition.data.imageName
                  }>
                  <span className={`definition ${lettersGroup.definition.data.kind.toLowerCase()}`}>
                    {lettersGroup.groups.reduce((acc: React.ReactNode[], val) => {
                      const res = val.letters.map((letter) => {
                        return (
                          <RenderLetter
                            key={letter.position}
                            enableCustomCssTooltip={enableCustomCssTooltip}
                            letter={letter}
                            i={counter++}
                            evenOdd={isEvenOdd(val.isEven)}
                            isPunctuation={!!checkIfPunctuation(letter.char)}
                            hasSyllable={
                              letter.syllableId !== undefined && letter.syllableId !== null
                            }
                          />
                        );
                      });
                      acc = acc.concat(res);
                      return acc;
                    }, [])}
                  </span>
                </Tooltip>
              );
            }
            return (
              <Fragment key={lettersGroup.letters[0].position}>
                {lettersGroup.letters.map((letter) => (
                  <RenderLetter
                    key={letter.position}
                    letter={letter}
                    i={counter++}
                    evenOdd={isEvenOdd(lettersGroup.isEven)}
                    isPunctuation={!!checkIfPunctuation(letter.char)}
                    hasSyllable={letter.syllableId !== undefined && letter.syllableId !== null}
                    enableCustomCssTooltip={enableCustomCssTooltip}
                    highlightSpaces={highlightSpaces}
                  />
                ))}
              </Fragment>
            );
          })}
    </Fragment>
  );
}, (previous, next) =>
  previous.enableCustomCssTooltip === next.enableCustomCssTooltip &&
  previous.highlightSpaces === next.highlightSpaces &&
  previous.groupedLetters === next.groupedLetters &&
  previous.letterParity === next.letterParity &&
  sameTextAppearance(previous.letters, next.letters),
);

export const RenderRhese = React.memo(
  ({
    children: rhese,
    activeStart,
    activeEnd,
    active,
    rheseProps,
    index,
    definitions,
    parity,
    letterParity,
    definitionMode = false,
    spaceIfNeeded,
    isSearchResult,
    enableCustomCssTooltip,
    highlightSpaces,
  }: {
    definitions: SubscribeToExtendedPage_extendedPage_definition[];
    parity?: ReadonlyMap<number, boolean>;
    letterParity?: readonly (boolean | undefined)[];
    definitionMode?: boolean;
    children: SubToPageContent_page_rheses;
    activeStart: boolean;
    activeEnd: boolean;
    active: boolean;
    rheseProps?: RheseOverrideProps;
    index: number;
    selectedSeparator?: string;
    spaceIfNeeded: React.ReactNode | null;
    isSearchResult: boolean;
    enableCustomCssTooltip: boolean;
    highlightSpaces: boolean;
  }) => {
    const isOdd = index === 0 || (index / 10) % 2 === 0;
    const onMouseUp = useCallback(() => {
      if (!rheseProps || !rheseProps.onMouseUp) return;
      rheseProps.onMouseUp(rhese);
    }, [rhese, rheseProps]);
    const onMouseDown = useCallback(() => {
      if (!rheseProps || !rheseProps.onMouseDown) return;
      rheseProps.onMouseDown(rhese);
    }, [rhese, rheseProps]);

    let warningLevel = "";
    const len = rhese.letters.length;
    if (len >= 31) warningLevel = "warningLight";
    if (len >= 36) warningLevel = "warning";
    if (len >= 40) warningLevel = "warningStrong";

    const props = {
      onMouseUp,
      onMouseDown,
      key: rhese.id,
      id: "r" + index,
      "data-id": rhese.id,
      "data-absolute-position": (rhese.letters as unknown as Letter[])[0]?.absoluteTextPosition,
      className: `
            rhese
            ${isOdd ? "rhese1" : "rhese2"}
            ${activeStart ? "activeStart" : ""} 
            ${activeEnd ? "activeEnd" : ""}
            ${active ? "active" : ""}
            ${highlightSpaces ? "borderGreen" : ""}
            ${warningLevel}
          `,
    };

    const resolvedParity = useMemo(() => {
      if (letterParity) return letterParity;
      const map = parity || syllableParity([rhese as unknown as Rhese]);
      return (rhese as unknown as Rhese).letters.map((letter) =>
        map.get(letter.absoluteTextPosition),
      );
    }, [letterParity, parity, rhese]);
    const convertedRhese = useMemo(
      () =>
        definitionMode
          ? convertRhese(rhese as unknown as Rhese, definitions, resolvedParity, true)
          : null,
      [rhese, definitions, resolvedParity, definitionMode],
    );

    const content = (
      <span className={`rheseWrapper ${isSearchResult ? "isSearchResult" : ""}`} data-rhese-length={len}>
        <span {...props}>
          <span className="rheseNumber">{index}</span>
          <RenderText
            groupedLetters={convertedRhese}
            letters={rhese.letters as unknown as Letter[]}
            letterParity={resolvedParity}
            enableCustomCssTooltip={enableCustomCssTooltip}
            highlightSpaces={highlightSpaces}
          />
        </span>
        {spaceIfNeeded}
      </span>
    );

    return (
      <Fragment>
        {content}
        {rhese.lineBreakAfter && (
          <span>
            ↲<br className="lineBreak" />
          </span>
        )}
      </Fragment>
    );
  },
);

type RheseOverrideProps = {
  onMouseUp?: (rhese: SubToPageContent_page_rheses) => void;
  onMouseDown?: (rhese: SubToPageContent_page_rheses) => void;
};

type BookProps<T> = {
  paragraphWrapper?: {
    component: JSXElementConstructor<
      T & {
        children: ReactElement;
        paragraphId: string;
        isLastParagraph: boolean;
      }
    >;
    props: Omit<T, "children" | "paragraphId" | "isLastParagraph">;
  };
  rheseWrapper?: (
    rhese: SubToPageContent_page_rheses,
    renderedRhese: ReactElement,
  ) => ReactElement | ReactElement[];
  children: SubscribeToExtendedPage_extendedPage;
  startRhese?: Element;
  endRhese?: Element;
  selectedRhesesIds?: string[];
  rheseProps?: RheseOverrideProps;
  selectedSeparator?: string;
  previousElementCount?: number;
  enableParagraphTooltip?: boolean;
  projectId: string;
  rheseOffset?: number;
  highlightSpaces?: boolean;
};

const RenderRoot = React.memo(
  ({
    rhese,
    definitions,
    letterParity,
    definitionMode,
    enableCustomCssTooltip,
    rheseCounter,
    activeStart,
    activeEnd,
    active,
    rheseProps,
    selectedSeparator,
    addSpaceAfter,
    rheseWrapper,
    isSearchResult,
    highlightSpaces,
  }: {
    rhese: SubToPageContent_page_rheses;
    definitions: SubscribeToExtendedPage_extendedPage_definition[];
    letterParity: readonly (boolean | undefined)[];
    definitionMode: boolean;
    enableCustomCssTooltip: boolean;
    rheseCounter: number;
    activeStart: boolean;
    activeEnd: boolean;
    active: boolean;
    rheseProps?: RheseOverrideProps;
    selectedSeparator?: string;
    addSpaceAfter: boolean;
    highlightSpaces: boolean;
    isSearchResult: boolean;
    rheseWrapper?: (
      rhese: SubToPageContent_page_rheses,
      renderedRhese: ReactElement,
    ) => ReactElement | ReactElement[];
  }) => {
    const spaceIfNeeded = addSpaceAfter ? <span className="space">&nbsp;</span> : null;

    const wrapper = rheseWrapper || ((_: any, renderedRhese: ReactElement) => renderedRhese);

    return (
      <Fragment>
        {wrapper(
          rhese,
          <RenderRhese
            isSearchResult={isSearchResult}
            definitions={definitions}
            letterParity={letterParity}
            definitionMode={definitionMode}
            index={rheseCounter}
            activeStart={activeStart}
            activeEnd={activeEnd}
            active={active}
            rheseProps={rheseProps}
            selectedSeparator={selectedSeparator}
            spaceIfNeeded={spaceIfNeeded}
            enableCustomCssTooltip={enableCustomCssTooltip}
            highlightSpaces={highlightSpaces}>
            {rhese}
          </RenderRhese>,
        )}
      </Fragment>
    );
  },
);

type Paragraph = {
  type: "paragraph";
  id: string;
  tagName?: string;
  rheses: SubToPageContent_page_rheses[];
};

const isParagraph = (p: any): p is Paragraph => p.type === "paragraph";

export default function <T>() {
  return React.memo(function RenderBook({
    children: pageMetadata,
    startRhese,
    endRhese,
    selectedRhesesIds,
    selectedSeparator,
    rheseProps,
    enableParagraphTooltip = false,
    rheseWrapper,
    rheseOffset = 0,
    paragraphWrapper,
    projectId,
    highlightSpaces,
  }: BookProps<T>) {
    // const rheseOffset = useContext(RheseOffset);
    let rheseCounter = rheseOffset;
    let paragraphCounter = 0;
    const location = useLocation();
    const definitionMode = location.pathname.includes("definition");
    const [t] = useI18n();
    const convert = useMemo(() => createPageConverter(), []);
    const present = useMemo(() => createRhesePresentations(), []);
    const { rheses: convertedRheses, paragraphs: convertedParagraphs } = useMemo(
      () => convert(projectId, pageMetadata),
      [pageMetadata, projectId, convert],
    );

    const paragraphsMap = useMemo(() => groupBy(convertedRheses, "paragraphId"), [convertedRheses]);

    const presentations = useMemo(
      () => present(convertedRheses as unknown as Rhese[], pageMetadata.definition, definitionMode),
      [convertedRheses, pageMetadata.definition, definitionMode, present],
    );
    const paragraphById = useMemo(
      () => new Map(convertedParagraphs.map((p) => [p.id, p])),
      [convertedParagraphs],
    );
    const contentWithParagraphs = useMemo(() => {
      const seen = new Set<string>();
      const content: (SubToPageContent_page_rheses | Paragraph)[] = [];
      for (const rhese of convertedRheses) {
        if (rhese.paragraphId === undefined) content.push(rhese);
        else if (!seen.has(rhese.paragraphId)) {
          seen.add(rhese.paragraphId);
          const paragraph = paragraphById.get(rhese.paragraphId);
          content.push({
            type: "paragraph",
            tagName: paragraph?.tag || "",
            id: rhese.paragraphId,
            rheses: paragraphsMap[rhese.paragraphId],
          });
        }
      }
      return content;
    }, [paragraphById, paragraphsMap, convertedRheses]);

    const { results: searchResults } = useContext(SearchContext);
    const searchIds = useMemo(
      () => new Set(searchResults.map((result) => result.rhese.data.id)),
      [searchResults],
    );
    const selectedIds = useMemo(() => new Set(selectedRhesesIds), [selectedRhesesIds]);

    const renderer = {
      rhese: (rhese: SubToPageContent_page_rheses) => {
        const activeStart = rhese.id === startRhese?.id;
        const activeEnd = rhese.id === endRhese?.id;
        const active = selectedIds.has(rhese.id);
        const isSearchResult = searchIds.has(rhese.id);

        return (
          <RenderRoot
            isSearchResult={isSearchResult}
            rheseWrapper={rheseWrapper}
            key={rhese.id}
            rheseCounter={(rheseCounter += 10)}
            rhese={rhese}
            definitions={presentations.get(rhese.id)!.definitions}
            letterParity={presentations.get(rhese.id)!.letterParity}
            enableCustomCssTooltip={isFormatTab(location.pathname)}
            definitionMode={definitionMode}
            activeStart={activeStart}
            activeEnd={activeEnd}
            active={active}
            addSpaceAfter={true}
            selectedSeparator={selectedSeparator}
            rheseProps={rheseProps}
            highlightSpaces={!!highlightSpaces}
          />
        );
      },
      paragraph: (p: Paragraph) => {
        const paragrapheKind = paragraphById.get(p.id)?.kind;

        paragraphCounter += 1;

        const labelKey = paragrapheKind || "NORMAL";

        const content = (
          <p
            id={`p${paragraphCounter}`}
            data-toto={`${p.id}`}
            className={`paragraph ${paragrapheKind}`}>
            {p.rheses.map((rhese) => (
              <Fragment key={rhese.id}>{renderer.rhese(rhese)}</Fragment>
            ))}
          </p>
        );

        return enableParagraphTooltip ? (
          <Tooltip
            title={
              <span>
                {t(ParagrapLabel[labelKey])}
                {p.tagName ? (
                  <>
                    <br />
                    {p.tagName}
                  </>
                ) : null}
              </span>
            }
            placement={"right"}>
            {content}
          </Tooltip>
        ) : (
          content
        );
      },
    };

    return (
      <>
        {contentWithParagraphs.map((content, i) => {
          if (isParagraph(content)) {
            const paragraph = renderer.paragraph(content);
            const Wrapper = paragraphWrapper?.component;
            const props = paragraphWrapper?.props;

            return (
              <Fragment key={content.id}>
                <div>
                  {Wrapper && props ? (
                    <Wrapper
                      {...(props as any)}
                      paragraphId={content.id}
                      isLastParagraph={i === contentWithParagraphs.length - 1}>
                      {paragraph}
                    </Wrapper>
                  ) : (
                    paragraph
                  )}
                </div>
              </Fragment>
            );
          }
        })}
      </>
    );
  });
}
